import { useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';
import {
	Answers,
	Application,
	Feedback,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	AvailabilityAfterProgramUIConfig,
	CharacterAndHabitsUIConfig,
	DietaryHabitsAndHealthUIConfig,
	InspirationAndWillingnessUIConfig,
	PracticeUIConfig,
	SupportUIConfig,
	UnderstandingOfHeartfulnessUIConfig,
} from '../../../shared/CategoryStepUIConfig';
import { CategoryScore, FinalRecommendationScore } from './CategoryScore';
import { ReviewAction } from '../../../shared/Constants';
import { isProposerSkippedToEnd } from '../../../shared/Utils';

interface InterviewResultProps {
	application?: Application;
	finalRecommendationText?: string;
}

export const InterviewResult = ({
	application,
	finalRecommendationText = 'preceptorRecommendation',
}: InterviewResultProps) => {
	const filterAnswersByCategory = useCallback(
		(categoryId: number, answers?: Answers) => {
			if (answers) {
				const categoryQuestionsAndAnswers =
					answers?.questionsAndAnswers?.filter(
						(question) => question.categoryId === categoryId
					);
				const answersByCategory: Answers = {
					language: answers.language,
					questionsAndAnswers: categoryQuestionsAndAnswers,
					finalComments: answers.finalComments,
				};
				return answersByCategory;
			}
		},
		[]
	);
	const applicationFeedback: Feedback = { review: ReviewAction.PENDING };
	const otherRefQuestionsAndAnswers =
		application?.otherReferenceInterviewFeedback?.answersToMandatoryQuestions
			?.questionsAndAnswers;
	const otherRefLastQuestionComment = otherRefQuestionsAndAnswers?.length
		? otherRefQuestionsAndAnswers[otherRefQuestionsAndAnswers.length - 1]
				.additionalComment
		: undefined;
	const otherRefRecommendationWithAnswer: Feedback = {
		...application?.otherReferenceRecommendation,
		comments: otherRefLastQuestionComment,
	};
	const isSkippedToEnd = isProposerSkippedToEnd(
		application?.proposerRecommendation
	);
	return (
		<div>
			<CategoryScore
				uiConfig={InspirationAndWillingnessUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					InspirationAndWillingnessUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.inspirationAndWillingness
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					InspirationAndWillingnessUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.inspirationAndWillingness
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					InspirationAndWillingnessUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={PracticeUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					PracticeUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.practice
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					PracticeUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.practice
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					PracticeUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={CharacterAndHabitsUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					CharacterAndHabitsUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.characterAndHabits
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					CharacterAndHabitsUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.characterAndHabits
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					CharacterAndHabitsUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
				familyMemberInterview={
					!isSkippedToEnd
						? application?.familyMemberInterviewFeedback?.characterAndHabits
						: undefined
				}
				familyMemberInterviewAnswers={filterAnswersByCategory(
					CharacterAndHabitsUIConfig.id,
					application?.familyMemberInterviewFeedback
						?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={AvailabilityAfterProgramUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					AvailabilityAfterProgramUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.availabilityAfterProgram
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					AvailabilityAfterProgramUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.availabilityAfterProgram
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					AvailabilityAfterProgramUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
				familyMemberInterview={
					!isSkippedToEnd
						? application?.familyMemberInterviewFeedback
								?.availabilityAfterProgram
						: undefined
				}
				familyMemberInterviewAnswers={filterAnswersByCategory(
					AvailabilityAfterProgramUIConfig.id,
					application?.familyMemberInterviewFeedback
						?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={SupportUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					SupportUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.support
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					SupportUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.support
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					SupportUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
				familyMemberInterview={
					!isSkippedToEnd
						? application?.familyMemberInterviewFeedback?.support
						: undefined
				}
				familyMemberInterviewAnswers={filterAnswersByCategory(
					SupportUIConfig.id,
					application?.familyMemberInterviewFeedback
						?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={UnderstandingOfHeartfulnessUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					UnderstandingOfHeartfulnessUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback
								?.understandingOfHeartfulness
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					UnderstandingOfHeartfulnessUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback
								?.understandingOfHeartfulness
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					UnderstandingOfHeartfulnessUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
			/>
			<CategoryScore
				uiConfig={DietaryHabitsAndHealthUIConfig}
				self={applicationFeedback}
				selfAnswers={filterAnswersByCategory(
					DietaryHabitsAndHealthUIConfig.id,
					application?.userAnswers
				)}
				applicantInterview={
					!isSkippedToEnd
						? application?.applicantInterviewFeedback?.dietaryHabitsAndHealth
						: undefined
				}
				applicantInterviewAnswers={filterAnswersByCategory(
					DietaryHabitsAndHealthUIConfig.id,
					application?.applicantInterviewFeedback?.answersToMandatoryQuestions
				)}
				preceptorInterview={
					!isSkippedToEnd
						? application?.preceptorInterviewFeedback?.dietaryHabitsAndHealth
						: undefined
				}
				preceptorInterviewAnswers={filterAnswersByCategory(
					DietaryHabitsAndHealthUIConfig.id,
					application?.preceptorInterviewFeedback?.answersToMandatoryQuestions
				)}
				familyMemberInterview={
					!isSkippedToEnd
						? application?.familyMemberInterviewFeedback?.dietaryHabitsAndHealth
						: undefined
				}
				familyMemberInterviewAnswers={filterAnswersByCategory(
					DietaryHabitsAndHealthUIConfig.id,
					application?.familyMemberInterviewFeedback
						?.answersToMandatoryQuestions
				)}
			/>
			<FinalRecommendationScore
				preceptor={
					!isSkippedToEnd ? application?.preceptorRecommendation : undefined
				}
				proposer={application?.proposerRecommendation}
				name={finalRecommendationText}
			/>
			{!isEmpty(application?.otherReferenceRecommendation) && (
				<FinalRecommendationScore
					otherReference={otherRefRecommendationWithAnswer}
					name={'otherReferenceRecommendation'}
				/>
			)}
		</div>
	);
};
